import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { removeChange, undoCaptionsTranslate } from "../utils";
import { PlayButton } from "./PlayButton";
import { FolderList } from "./FolderList";
import { SaveButton } from "./SaveButton";
import { Ico } from "./Uikit";
import { useAuth } from "../providers/AuthProvider";

function getStageStyle(stage) {
  if (stage.total === 0) {
    return { color: "#8c8c8c", backgroundColor: "#f5f5f5", borderColor: "#d9d9d9" };
  }
  if (stage.done === stage.total) {
    return { color: "#52c41a", backgroundColor: "#f6ffed", borderColor: "#b7eb8f" };
  }
  if (stage.done > 0 || stage.inProgress > 0) {
    return { color: "#fa8c16", backgroundColor: "#fff7e6", borderColor: "#ffd591" };
  }
  return { color: "#8c8c8c", backgroundColor: "#f5f5f5", borderColor: "#d9d9d9" };
}

function getStageText(stage) {
  if (stage.total === 0) return `${stage.label}`;
  if (stage.done === stage.total) return `${stage.label} готово ${stage.done}`;
  if (stage.inProgress > 0) return `${stage.label} в процессе ${stage.done}/${stage.total}`;
  if (stage.done > 0) return `${stage.label} готово ${stage.done}/${stage.total}`;
  return `${stage.label}`;
}

const languages = {
  english: {
    title: "En",
    titleLong: "английский",
  },
  de: {
    title: "De",
    titleLong: "немецкий",
  },
  es: {
    title: "Es",
    titleLong: "испанский",
  },
  chinese: {
    title: "汉",
    titleLong: "китайский",
  },
};

export function Head({
  playShown = true,
  playSpacebar = true,
  titleShown = true,
  foldersShown = true,
  languageShown = true,
  saveShown = "changed",
  videoToggleShown = true,
  context,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const handleLanguageChange = (value) => {
    context.handles.setToLang(value);
  };

  const fixName = (name) => {
    if (name && typeof name === "string") {
      name = name.replaceAll("_", " ");
      return name;
    } else {
      return "Lecture without a name";
    }
  };

  const fixLang = (lang) => {
    if (!lang || !languages[lang]) return;
    return `Рус → ${languages[lang]["title"]}`;
  };

  const handleVideo = () => {
    const currentUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(currentUrl.search);

    if (context.state.video) {
      // Remove the 'video' parameter if it exists
      searchParams.delete("video");
      context.handles.setVideo();
    } else {
      // Add the 'video' parameter
      searchParams.set("video", "1");
      context.handles.setVideo("sm");
    }

    // Construct the new path
    const newPath = `${location.pathname}?${searchParams.toString()}`;
    navigate(newPath);
  };

  const undoChange = () => {
    const found = context.state[saveShown].findLast(
      (item) => item.action === "text" && item.id.startsWith(`${context.state.folder}_`),
    );
    const foundId = found.id.split("_");
    undoCaptionsTranslate(
      {
        id: found.id,
        folder_num: foundId[0],
        original_id: foundId[1],
      },
      found.prevText,
      context,
    );
    removeChange(found, "changedTranslation", context);
  };

  return (
    <>
      <div className="x_head0">
        <div className="x_head_border" />

        {titleShown && (
          <div className="x_head_title_row">
            {fixName(context.state["name"] || context.state.lectureName)}
            {isAdmin && context.state.lectureStatus?.length > 0 && (
              <span style={{ marginLeft: "10px", display: "inline-flex", gap: "4px", flexWrap: "wrap" }}>
                {context.state.lectureStatus.map((stage) => {
                  const style = getStageStyle(stage);
                  return (
                    <span
                      key={stage.key}
                      style={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        borderRadius: "4px",
                        border: `1px solid ${style.borderColor}`,
                        backgroundColor: style.backgroundColor,
                        color: style.color,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getStageText(stage)}
                    </span>
                  );
                })}
              </span>
            )}
          </div>
        )}

        <div className="x_head_controls_row">
        <div className="x_head_l">
          <div className="x_head_logo">
            {playShown && (
              <div className="x_player_control">
                <PlayButton
                  playSpacebar={playSpacebar}
                  enter={context.state.pop ? false : true}
                  context={context}
                />
              </div>
            )}

            {foldersShown && (
              <div className="x_head_chapters0">
                <FolderList
                  foldersTotal={context.state.foldersTotal}
                  activeFolder={parseInt(context.state.folder)}
                />
              </div>
            )}

            {languageShown && (
              <div className="x_head_logo_lang">{fixLang(context.state["lang"])}</div>
            )}
          </div>
          <div className="x_head_center"></div>
        </div>

        <div className="x_head_r">
          {location.pathname.includes("translate") && (
            <a
              href={`/subtitles/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
            >
              Субтитры
            </a>
          )}
          {context.state.subtitleMode && (
            <>
              <select
                style={{ width: "180px" }}
                value={context.state.toLang}
                onChange={(e) => {
                  handleLanguageChange(e.target.value);
                }}
              >
                {Object.keys(languages).map((val) => {
                  return <option value={val}>{languages[val].titleLong}</option>;
                })}
              </select>
              {!context.state.lang || context.state.lang === "ru" ? (
                <>
                  <button
                    onClick={() =>
                      context.handles.metadataTranslate(context.state.folder, context.state.toLang)
                    }
                  >
                    Создать перевод (использует платные ресурсы)
                  </button>
                  {location.pathname.includes("subtitles") && (
                    <a
                      href={`/translate/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
                    >
                      Перевод
                    </a>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() =>
                      context.handles.audioSynthesize(
                        null,
                        context.state.folder,
                        context.state.mediaId,
                        context.state.lang,
                      )
                    }
                  >
                    Создать озвучку (использует платные ресурсы)
                  </button>
                  {location.pathname.includes("subtitles") && (
                    <a
                      href={`/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
                    >
                      Озвучка
                    </a>
                  )}
                </>
              )}
            </>
          )}

          {saveShown && (
            <>
              <button
                onClick={undoChange}
                title="Отменить изменение"
                disabled={
                  context.state.savingStatus ||
                  context.state[saveShown].filter(
                    (item) =>
                      item.action === "text" && item.id.startsWith(`${context.state.folder}_`),
                  ).length === 0
                }
              >
                <Ico>undo</Ico>
              </button>
              <div className="x_changes_count">
                <div className="x_flex-h">
                  <div>Изменений:</div>
                  <strong>{context.state[saveShown].length}</strong>
                </div>
              </div>

              <SaveButton context={context} />
            </>
          )}

          {/* <div >
            <div className={`x_player_video_set_1 ${context.state.russian ? 'active' : ''}`}
              // onClick={() => context.handles.setVideo(context.state.video ? false : 'sm')}
              onClick={() => {
                context.handles.setRussian(r => !r)
              }}
            >
              Ру
            </div>
          </div> */}

          {videoToggleShown && (
            <div>
              <div
                className={`x_player_video_set_1 ${context.state.video ? "active" : ""}`}
                // onClick={() => context.handles.setVideo(context.state.video ? false : 'sm')}
                onClick={() => {
                  handleVideo();
                }}
              >
                <div className="ico">videocam</div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
